import React, { useContext, useState, useMemo } from "react";
import {
  Container,
  Table,
  Button,
  InputGroup,
  FormControl,
  Spinner,
} from "react-bootstrap";
import "./certification-of-holdings-container.scss";
import ReportsService from "../../../../../../services/reports-service";
import { IdentityContext } from "../../../../../../context/identity-context";
import UploadModal from "./components/modal/upload-modal/upload-modal";
import ReportCommentModal from "./components/modal/report-comment-modal/report-comment-modal";
import PublishReportModal from "./components/modal/publish-report-modal/publish-report-modal";
import RejectReportModal from "./components/modal/reject-report-modal/reject-report-modal";
import PdfPreviewModal from "./components/modal/pdf-preview-modal/pdf-preview-modal";
import FinalPublishSuccessModal from "./components/modal/final-publish-success-modal/final-publish-success-modal";

const CertificationOfHoldingsContainer = ({ items }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [openRows, setOpenRows] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [checkedReportIndexes, setCheckedReportIndexes] = useState(new Set());
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState("");
  const [sendingReports, setSendingReports] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPdfPreviewModal, setShowPdfPreviewModal] = useState(false);
  const [isPublishSuccess, setIsPublishSuccess] = useState(false);
  const [publishSuccessMessage, setPublishSuccessMessage] = useState("");
  const [isRejectSuccess, setIsRejectSuccess] = useState(false);
  const [rejectSuccessMessage, setRejectSuccessMessage] = useState("");
  const [comments, setComments] = useState("");
  const [pdfPreviewUrls, setPdfPreviewUrls] = useState([]);
  const [currentPdfIndex, setCurrentPdfIndex] = useState(0);
  const [isFinalPublishSuccess, setIsFinalPublishSuccess] = useState(false);
  const [finalPublishSuccessMessage, setFinalPublishSuccessMessage] = useState("");

  const reportService = new ReportsService();
  const { user } = useContext(IdentityContext);
  const checkerName = user.attributes["custom:display_name"];

  const canInteractReports = useMemo(() => {
    return Array.from(checkedReportIndexes).every((id) => {
      const selectedItem = items.find(
        (item) => item.id.toString() === id.toString()
      );
      return selectedItem ? selectedItem.status === "For Checking" : false;
    });
  }, [checkedReportIndexes, items]);

  const handleRowClick = (itemId) => {
    setOpenRows((prevOpenRows) =>
      prevOpenRows.includes(itemId)
        ? prevOpenRows.filter((id) => id !== itemId)
        : [...prevOpenRows, itemId]
    );
  };

  const handleUploadClick = (report) => {
    setSelectedReport(report);
    setShowUploadModal(true);
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setSelectedReport(null);
  };

  const handleCheckboxChange = (itemId, isChecked) => {
    setCheckedReportIndexes((prevIndexes) => {
      const updatedIndexes = new Set(prevIndexes);
      if (isChecked) {
        updatedIndexes.add(itemId);
      } else {
        updatedIndexes.delete(itemId);
      }
      return new Set(updatedIndexes);
    });
  };

  const handleCommentClick = (comment) => {
    setSelectedComment(comment);
    setShowCommentModal(true);
  };

  const handlePreviewClick = async (report) => {
    try {
      const response = await reportService.retrieveReport(
        report.reportType,
        report.bpId,
        report.reportTitle,
        report.sellingAgentRequestDate
      );
      const newWindow = window.open(
        response.fileURL,
        "_blank",
        "noopener,noreferrer"
      );
      if (newWindow) newWindow.opener = null;
    } catch (error) {
      console.error("Error fetching report preview:", error.message);
    }
  };

  const handleDownloadClick = async (report) => {
    try {
      const response = await reportService.retrieveReport(
        report.reportType,
        report.bpId,
        report.reportTitle,
        report.sellingAgentRequestDate
      );
      const url = response.fileURL;
      const filename =
        response.fileName || url.substring(url.lastIndexOf("/") + 1);
      const fileResponse = await fetch(url);
      if (!fileResponse.ok) throw new Error("Network response was not ok");
      const blob = await fileResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading report:", error.message);
    }
  };

  const handleRejectClick = () => {
    if (checkedReportIndexes.size > 0 && canInteractReports) {
      setShowRejectModal(true);
    }
  };

  const handleSendClick = () => {
    if (checkedReportIndexes.size > 0 && canInteractReports) {
      setShowPublishModal(true);
    }
  };

  const handleCancel = () => {
    setShowPublishModal(false);
    setShowRejectModal(false);
    setShowPdfPreviewModal(false);
    setComments("");
    setPublishSuccessMessage("");
    setRejectSuccessMessage("");
  };

  const handlePublishContinue = async () => {
    try {
      const pdfUrls = [];
      for (const itemId of Array.from(checkedReportIndexes)) {
        const report = items.find(
          (item) => item.id.toString() === itemId.toString()
        );
        if (report) {
          const response = await reportService.retrieveReport(
            report.reportType,
            report.bpId,
            report.reportTitle,
            report.sellingAgentRequestDate
          );
          pdfUrls.push({ url: response.fileURL, title: report.reportTitle });
        }
      }
      setPdfPreviewUrls(pdfUrls);
      setShowPdfPreviewModal(true);
      setShowPublishModal(false);
    } catch (error) {
      console.error("Error fetching PDF previews:", error);
      setPublishSuccessMessage("Some reports failed to load.");
      setIsPublishSuccess(false);
    }
  };

  const handleFinalPublish = async () => {
    let successCount = 0;
    const totalReports = checkedReportIndexes.size;
    try {
      for (const itemId of Array.from(checkedReportIndexes)) {
        const report = items.find(
          (item) => item.id.toString() === itemId.toString()
        );
        if (report) {
          await reportService.rrCheckerSubmitCertificationOfHoldings(
            report.bpId,
            report.sellingAgentRequestDate,
            report.sellingAgentEmailAddress,
            checkerName
          );
          successCount++;
        }
      }
      if (successCount === totalReports) {
        setFinalPublishSuccessMessage(
          `Certification of Holdings Successfully Submitted\n${totalReports} confirmations sent.`
        );
      } else {
        setFinalPublishSuccessMessage(
          `Some Certification of Holdings were not submitted. Successfully submitted ${successCount} out of ${totalReports}.`
        );
      }
      setIsFinalPublishSuccess(true);
      setShowPdfPreviewModal(false);
    } catch (error) {
      console.error("Error during final publish:", error);
      setFinalPublishSuccessMessage("Failed to publish some reports.");
      setIsFinalPublishSuccess(true);
      setShowPdfPreviewModal(false);
    }
  };

  const handleRejectContinue = async () => {
    try {
      for (const itemId of Array.from(checkedReportIndexes)) {
        const report = items.find(
          (item) => item.id.toString() === itemId.toString()
        );
        if (report) {
          await reportService.rrCheckerRejectCertificationOfHoldings(
            report.bpId,
            report.sellingAgentRequestDate,
            comments,
            report.sellingAgentEmailAddress,
            checkerName
          );
        }
      }
      setRejectSuccessMessage("Reports successfully rejected!");
      setIsRejectSuccess(true);
      setShowRejectModal(false);
      setShowRejectModal(true);
    } catch (error) {
      console.error("Error rejecting reports:", error);
      setRejectSuccessMessage("Failed to reject reports.");
      setIsRejectSuccess(false);
    }
  };

  // Sorting: sort items by sellingAgentRequestDate
  const sortedItems = [...items].sort((a, b) => {
    const dateA = a.sellingAgentRequestDate
      ? new Date(a.sellingAgentRequestDate)
      : new Date(0);
    const dateB = b.sellingAgentRequestDate
      ? new Date(b.sellingAgentRequestDate)
      : new Date(0);
    if (isNaN(dateA) || isNaN(dateB)) return 0;
    return sortConfig?.direction === "asc" ? dateA - dateB : dateB - dateA;
  });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Update filtering logic to search every column.
  // For this table the columns are:
  // - Report Title (from reportTitle)
  // - From (from sellingAgentEmailAddress)
  // - BPID (from bpId)
  // - Date of Request (from sellingAgentRequestDate)
  // - Status (from status)
  const filteredItems = sortedItems.filter((item) => {
    const reportTitle = (item.reportTitle || "").toString().toLowerCase();
    const from = (item.sellingAgentEmailAddress || "").toString().toLowerCase();
    const bpId = (item.bpId || "").toString().toLowerCase();
    const requestDate = (item.sellingAgentRequestDate || "")
      .toString()
      .toLowerCase();
    const status = (item.status || "").toString().toLowerCase();
    const searchLower = searchTerm.toLowerCase();

    return (
      reportTitle.includes(searchLower) ||
      from.includes(searchLower) ||
      bpId.includes(searchLower) ||
      requestDate.includes(searchLower) ||
      status.includes(searchLower)
    );
  });

  return (
    <Container className="report-container">
      <InputGroup className="mb-3">
        <FormControl
          placeholder="Search..."
          aria-label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </InputGroup>

      <div className="content-wrapper">
        <div className="content-scrollable">
          <Table bordered>
            <thead>
              <tr>
                <th></th>
                <th>REPORT NAME</th>
                <th>FROM</th>
                <th>BPID</th>
                <th
                  onClick={() => handleSort("paymentDate")}
                  style={{ cursor: "pointer" }}
                >
                  Date of Request{" "}
                  {sortConfig.key === "paymentDate"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <React.Fragment key={item.id}>
                  <tr onClick={() => handleRowClick(item.id)}>
                    <td>
                      <input
                        type="checkbox"
                        checked={checkedReportIndexes.has(item.id)}
                        onChange={(e) =>
                          handleCheckboxChange(item.id, e.target.checked)
                        }
                        disabled={item.status !== "For Checking"}
                      />
                    </td>
                    <td>{item.reportTitle}</td>
                    <td>{item.sellingAgentEmailAddress}</td>
                    <td>{item.bpId}</td>
                    <td>{item.sellingAgentRequestDate}</td>
                    <td>
                      {item.status}
                      {item.comment ? (
                        <span
                          style={{ cursor: "pointer", marginLeft: "5px" }}
                          onClick={() => handleCommentClick(item.comment)}
                        >
                          ▼
                        </span>
                      ) : (
                        ""
                      )}
                    </td>
                    <td>
                      {(item.status === "For Checking" ||
                        item.status === "For Adjustment") && (
                        <>
                          {item.status === "For Checking" && (
                            <Button
                              variant="primary"
                              className="action-button"
                              onClick={() => handleUploadClick(item)}
                            >
                              Upload
                            </Button>
                          )}
                          <Button
                            variant="primary"
                            className="action-button"
                            onClick={() => handlePreviewClick(item)}
                          >
                            Preview
                          </Button>
                          <Button
                            variant="primary"
                            className="action-button"
                            onClick={() => handleDownloadClick(item)}
                          >
                            Download
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      <div className="action-buttons">
        <Button
          variant="primary"
          onClick={handleRejectClick}
          disabled={checkedReportIndexes.size === 0 || !canInteractReports}
        >
          Reject
        </Button>
        <Button
          variant="primary"
          onClick={handleSendClick}
          disabled={
            checkedReportIndexes.size === 0 ||
            !canInteractReports ||
            sendingReports
          }
        >
          {sendingReports ? (
            <Spinner animation="border" style={{ width: "20px", height: "20px" }} />
          ) : (
            "Send Reports"
          )}
        </Button>
      </div>

      {selectedReport && (
        <UploadModal
          show={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          selectedReport={selectedReport}
        />
      )}

      <ReportCommentModal
        show={showCommentModal}
        onClose={() => setShowCommentModal(false)}
        comment={selectedComment}
      />

      <PublishReportModal
        show={showPublishModal}
        isPublishSuccess={isPublishSuccess}
        publishSuccessMessage={publishSuccessMessage}
        handleCancel={handleCancel}
        handlePublishContinue={handlePublishContinue}
      />

      <RejectReportModal
        show={showRejectModal}
        isRejectSuccess={isRejectSuccess}
        rejectSuccessMessage={rejectSuccessMessage}
        comments={comments}
        setComments={setComments}
        handleCancel={handleCancel}
        handleRejectContinue={handleRejectContinue}
      />

      <PdfPreviewModal
        show={showPdfPreviewModal}
        pdfPreviewUrls={pdfPreviewUrls}
        currentPdfIndex={currentPdfIndex}
        setCurrentPdfIndex={setCurrentPdfIndex}
        handleCancel={handleCancel}
        handleFinalPublish={handleFinalPublish}
      />

      <FinalPublishSuccessModal
        show={isFinalPublishSuccess}
        onHide={() => setIsFinalPublishSuccess(false)}
        finalPublishSuccessMessage={finalPublishSuccessMessage}
      />
    </Container>
  );
};

export default CertificationOfHoldingsContainer;
